// utils/authUtils.js - Authentication utility functions

import { resetCRMWidget } from './crmWidgetUtils';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;

/**
 * Check if JWT token is expired or will expire soon
 * @param {number} bufferMinutes - Minutes before expiration to consider token "expiring"
 * @returns {boolean} - True if token is expired or will expire within buffer
 */
export const isTokenExpiring = (bufferMinutes = 5) => {
  const expiresAt = localStorage.getItem("ogestyle_token_expires");
  if (!expiresAt) return true;

  const expirationDate = new Date(expiresAt);
  const now = new Date();
  const bufferMs = bufferMinutes * 60 * 1000;

  return expirationDate.getTime() - now.getTime() < bufferMs;
};

/**
 * Check if user is authenticated with a valid token
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem("ogestyle_token");
  if (!token) return false;

  // Check if token is expired
  if (isTokenExpiring(0)) {
    // Token expired, clear it
    logout();
    return false;
  }

  return true;
};

/**
 * Refresh JWT token before it expires
 * @returns {Promise<boolean>} - True if refresh successful
 */
export const refreshToken = async () => {
  try {
    const currentToken = localStorage.getItem("ogestyle_token");
    if (!currentToken) return false;

    const rememberMe = localStorage.getItem("ogestyle_remember_me") === "true";

    const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentToken}`,
      },
      body: JSON.stringify({ rememberMe }),
    });

    if (!response.ok) {
      throw new Error("Token refresh failed");
    }

    const data = await response.json();

    // Update stored token and expiration
    localStorage.setItem("ogestyle_token", data.token);
    if (data.expiresAt) {
      localStorage.setItem("ogestyle_token_expires", data.expiresAt);
    }
    return true;
  } catch (error) {
    logout();
    return false;
  }
};

/**
 * Setup automatic token refresh
 * Checks token expiration every minute and refreshes if needed
 */
export const setupAutoRefresh = () => {
  // Check every minute
  const intervalId = setInterval(async () => {
    if (isAuthenticated() && isTokenExpiring(10)) {
      // Token expires in less than 10 minutes, refresh it
      await refreshToken();
    }
  }, 60 * 1000); // 1 minute

  // Return cleanup function
  return () => clearInterval(intervalId);
};

/**
 * Logout user and clear all auth data
 */
export const logout = () => {
  // Reset CRM widget session first
  resetCRMWidget();

  // Clear all OGESTYLE scoped localStorage keys so no role/user context leaks
  // across sessions on shared browsers (includes role override/state).
  const localKeys = Object.keys(localStorage || {});
  localKeys.forEach((key) => {
    if (key.startsWith("ogestyle_")) {
      localStorage.removeItem(key);
    }
  });

  // Clear transient non-prefixed keys that can still reference previous users.
  localStorage.removeItem("bookingImportMapping");

  // Clear all OGESTYLE scoped sessionStorage keys.
  const sessionKeys = Object.keys(sessionStorage || {});
  sessionKeys.forEach((key) => {
    if (key.startsWith("ogestyle_")) {
      sessionStorage.removeItem(key);
    }
  });

  // Clear common redirects/flags that can affect next login context.
  sessionStorage.removeItem("redirectAfterLogin");

  // Notify SPA listeners that auth state was reset.
  window.dispatchEvent(new Event("authStateChanged"));

  // Redirect to login page
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

/**
 * Get or create guest session ID for cart tracking
 * @returns {string} - Session ID
 */
export const getOrCreateSessionId = () => {
  let sessionId = localStorage.getItem("ogestyle_cart_session");
  if (!sessionId) {
    sessionId = `guest_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    localStorage.setItem("ogestyle_cart_session", sessionId);
  }
  return sessionId;
};

/**
 * Clear guest session ID (call after successful login/cart migration)
 */
export const clearSessionId = () => {
  localStorage.removeItem("ogestyle_cart_session");
};

/**
 * Get authentication headers for API requests
 * @returns {Object} - Headers object with Authorization token
 */
export const getAuthHeaders = () => {
  const token = localStorage.getItem("ogestyle_token");
  const sessionId = getOrCreateSessionId();

  const headers = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  } else {
    headers["x-session-id"] = sessionId;
  }

  return headers;
};

/**
 * Get API base URL from environment
 * @returns {string} - API base URL
 */
export const getApiBaseUrl = () => {
  return API_BASE_URL;
};

/**
 * Make authenticated API request with automatic token refresh
 * @param {string} url - API endpoint URL
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>}
 */
export const authenticatedFetch = async (url, options = {}) => {
  // Check if token needs refresh before request
  if (isAuthenticated() && isTokenExpiring(5)) {
    await refreshToken();
  }

  // Make request with auth headers
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  // If unauthorized, try refreshing token and retry once
  if (response.status === 401) {
    const refreshed = await refreshToken();
    if (refreshed) {
      // Retry request with new token
      return fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      });
    } else {
      // Refresh failed, logout
      logout();
    }
  }

  return response;
};

const authUtils = {
  isTokenExpiring,
  isAuthenticated,
  refreshToken,
  setupAutoRefresh,
  logout,
  getAuthHeaders,
  getApiBaseUrl,
  authenticatedFetch,
};

export default authUtils;
